<template>
  <v-container fluid>
    <v-row
      class="match-height align-stretch justify-start"
    >
      <template v-if="loading">
        <loading-spinner class="mx-auto" />
      </template>
      <template v-else-if="widgetData">
        <template
          v-for="widget in orderedWidgets"
          :key="widget.key"
        >
          <widget-course-session-invite
            v-if="widget.key === 'courseSessionInvite' && widgetData?.nextCourseSession"
            :course-session="widgetData.nextCourseSession"
          />
          <widget-course-session-confirmation
            v-else-if="widget.key === 'courseSessionConfirmation' && widgetData?.courseSessionPendingConfirmation"
            :course-session="widgetData.courseSessionPendingConfirmation"
          />
          <widget-alert-messages
            v-else-if="widget.key === 'alertMessages' && widgetData?.alertMessages?.length"
            :alert-messages="widgetData.alertMessages"
          />
          <widget-user-profile
            v-else-if="widget.key === 'userProfile'"
            :bg-image="widgetData?.heroImage"
            :profile-image-status="widgetData.profileImageStatus"
          />
          <widget-welcome-text
            v-else-if="widget.key === 'welcomeText' && widgetData?.welcomeText"
            :welcome-text="widgetData.welcomeText"
          />
          <widget-shift
            v-else-if="widget.key === 'currentShift' && widgetData?.currentAssignedShift"
            :caption="t('shift.current')"
            :shift="widgetData.currentAssignedShift"
          />
          <widget-shift
            v-else-if="widget.key === 'nextShift' && widgetData?.nextAssignedShift"
            :caption="t('shift.my_next')"
            :shift="widgetData.nextAssignedShift"
          />
          <widget-current-need
            v-else-if="widget.key === 'currentNeed' && can('submit_availability')"
          />
          <widget-cart-display
            v-else-if="widget.key === 'cartDisplay' && can('access_cart_page')"
          />
          <widget-experience
            v-else-if="widget.key === 'experience' && canSeeExperiences"
            :experience="widgetData.todaysExperience"
          />
        </template>
      </template>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import WidgetAlertMessages from '@/components/home/widgets/WidgetAlertMessages.vue';
import WidgetCartDisplay from '@/components/home/widgets/WidgetCartDisplay.vue';
import WidgetCourseSessionConfirmation from '@/components/home/widgets/WidgetCourseSessionConfirmation.vue';
import WidgetCourseSessionInvite from '@/components/home/widgets/WidgetCourseSessionInvite.vue';
import WidgetCurrentNeed from '@/components/home/widgets/WidgetCurrentNeed.vue';
import WidgetExperience from '@/components/home/widgets/WidgetExperience.vue';
import WidgetShift from '@/components/home/widgets/WidgetShift.vue';
import WidgetUserProfile from '@/components/home/widgets/WidgetUserProfile.vue';
import WidgetWelcomeText from '@/components/home/widgets/WidgetWelcomeText.vue';
import { usePermissions } from '@/composables';
import { useErrorStore } from '@/stores/errors';

const DEFAULT_WIDGET_ORDER = [
  'courseSessionInvite',
  'courseSessionConfirmation',
  'alertMessages',
  'userProfile',
  'welcomeText',
  'currentShift',
  'nextShift',
  'currentNeed',
  'cartDisplay',
  'experience',
];

interface WidgetConfig {
  key: string;
  enabled: boolean;
}

const { t } = useI18n();
const { handleError } = useErrorStore();
const { can } = usePermissions();
const widgetData = ref<any>(null);
const loading = ref(true);
const canSeeExperiences = computed<boolean>(() => can('access_experiences_page') && widgetData.value?.todaysExperience);

const orderedWidgets = computed<WidgetConfig[]>(() => {
  const config: WidgetConfig[] | undefined = widgetData.value?.widgetConfig;
  if (!config || config.length === 0) {
    return DEFAULT_WIDGET_ORDER.map(key => ({ key, enabled: true }));
  }
  return config.filter(w => w.enabled);
});

async function loadWidgetData() {
  try {
    const response = await axios.get('/api/widgetdata');
    widgetData.value = response.data.data;
  }
  catch (error) {
    console.error(error);
    handleError(error, t('errors.cannot_load_widgets'));
  }
  finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await loadWidgetData();
});
</script>
